/**
 * LlmKeysPage — Admin-only. Centralized API key management for OpenAI / Anthropic / Gemini / fal.ai.
 * Keys are stored Fernet-encrypted in MongoDB. Falls back to Emergent Universal Key if not set.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Loader2, Save, ShieldCheck, AlertTriangle, Trash2, CheckCircle2, XCircle, Zap, Cpu, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SENTINEL = "********";

const PROVIDER_HINTS = {
  openai: { url: "https://platform.openai.com/api-keys", placeholder: "sk-…" },
  anthropic: { url: "https://console.anthropic.com/settings/keys", placeholder: "sk-ant-…" },
  gemini: { url: "https://aistudio.google.com/apikey", placeholder: "AIza…" },
  falai: { url: "https://fal.ai/dashboard/keys", placeholder: "fal-…" },
  runway: { url: "https://dev.runwayml.com", placeholder: "key_…" },
};

function TransferCard() {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [withSecrets, setWithSecrets] = useState(true);

  const doExport = async () => {
    if (!pw.trim()) { toast.info("Bitte dein Admin-Passwort eingeben"); return; }
    setBusy("export");
    try {
      const r = await axios.post(`${API}/admin/transfer/export`,
        { password: pw, include_secrets: withSecrets }, { withCredentials: true });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invictaflow_export_${withSecrets ? "VOLL_" : ""}${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const counts = Object.entries(r.data.counts || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Export heruntergeladen (${counts})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Export fehlgeschlagen"); }
    setBusy("");
  };

  const doImport = async () => {
    if (!pw.trim()) { toast.info("Bitte dein Admin-Passwort eingeben"); return; }
    if (!importFile) { toast.info("Bitte Export-Datei auswaehlen"); return; }
    setBusy("import");
    try {
      const text = await importFile.text();
      const data = JSON.parse(text);
      const r = await axios.post(`${API}/admin/transfer/import`, { password: pw, data }, { withCredentials: true });
      const res = Object.entries(r.data.imported || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      if (r.data.error_count) {
        toast.warning(`Import abgeschlossen mit ${r.data.error_count} Fehlern (${(r.data.errors || []).slice(0, 3).join("; ")})`);
      } else {
        toast.success(`Import abgeschlossen (${res})`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      toast.error(detail ? `Import fehlgeschlagen: ${detail}` : `Import fehlgeschlagen${status ? ` (HTTP ${status})` : " (Server nicht erreichbar oder Serverfehler)"} — gueltige Export-JSON-Datei?`);
    }
    setBusy("");
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="transfer-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#2563EB]" /> Daten-Export / Import (Preview ↔ Live)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-[var(--studio-text-muted)]">
          Exportiert ALLE Studio-Daten als JSON: Firmen (inkl. Pixel-IDs, Geo, Filter), Content &amp; Posts, Mediathek-Einträge, KI-Vorlagen &amp; Blueprints,
          Kampagnen, Leads, Wettbewerber, Tracking-Links, Review-Links, Cockpit-Ideen, Autopilot-Historie, Webhooks, Model-Routing und Brand Kits.
          Nicht enthalten: Besucher-Rohdaten (Analytics) und Logs. In der Live-App dieselbe Datei hier hochladen und importieren.
          Passwortgeschuetzt: Dein Admin-Login-Passwort ist erforderlich.
        </p>
        <label className="flex items-start gap-2 cursor-pointer text-[11px] p-2 rounded-md border border-amber-200 bg-amber-50/50">
          <input type="checkbox" checked={withSecrets} onChange={(e) => setWithSecrets(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#2563EB] mt-0.5" data-testid="transfer-secrets-checkbox" />
          <span>
            <b>Voll-Export inkl. Secrets:</b> API-Keys (OpenAI/Gemini/SEMrush), OAuth-App-Zugangsdaten (Meta/TikTok/LinkedIn Developer-Apps),
            Meta/TikTok/LinkedIn-Verbindungen &amp; Seiten-Tokens, Google-Ads-Einstellungen (inkl. Developer-Token), KI-Berechtigungen,
            RSS-Feeds, Firmen-Radar und Benutzer-Logins. Funktioniert auch, wenn Live einen anderen Verschluesselungs-Schluessel hat (Werte werden beim Import neu verschluesselt).
            <span className="text-amber-700"> Datei sicher aufbewahren — sie enthaelt Zugangsdaten im Klartext!</span>
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Admin-Passwort zur Bestaetigung" className="w-64 text-xs" data-testid="transfer-password" />
          <Button size="sm" onClick={doExport} disabled={busy === "export"}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="transfer-export-btn">
            {busy === "export" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Export herunterladen
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[var(--studio-border)]">
          <input type="file" accept=".json" onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            className="text-xs" data-testid="transfer-import-file" />
          <Button size="sm" variant="outline" onClick={doImport} disabled={busy === "import"} data-testid="transfer-import-btn">
            {busy === "import" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Datei importieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SemrushKeyCard() {
  const [info, setInfo] = useState(null);
  const [val, setVal] = useState("");
  const load = () => axios.get(`${API}/admin/semrush-key`, { withCredentials: true }).then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!val.trim()) { toast.info("Bitte API-Key eintragen"); return; }
    try {
      await axios.put(`${API}/admin/semrush-key`, { api_key: val.trim() }, { withCredentials: true });
      toast.success("SEMrush API-Key gespeichert");
      setVal("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/semrush-key`, { withCredentials: true });
      toast.success("Entfernt");
      load();
    } catch { toast.error("Fehler"); }
  };
  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="provider-card-semrush">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#FF642D]" /> SEMrush (Website & SEO)</span>
          {info?.configured ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid="badge-configured-semrush">Konfiguriert</Badge>
          ) : (
            <Badge variant="outline" className="text-[var(--studio-text-muted)]">Nicht konfiguriert</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input type="password" value={val} onChange={(e) => setVal(e.target.value)}
            placeholder="SEMrush API-Key" className="flex-1 min-w-[260px] font-mono text-xs" data-testid="input-key-semrush" />
          <Button size="sm" onClick={save} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-semrush-btn">Speichern</Button>
          {info?.configured && (
            <Button variant="ghost" size="sm" onClick={remove} className="text-red-500" data-testid="remove-semrush-btn"><Trash2 className="w-3 h-3" /></Button>
          )}
        </div>
        <p className="text-[11px] text-[var(--studio-text-muted)]">
          {info?.configured && <>Vorschau: <code>{info.preview}</code> · </>}
          Wird fuer SEO-Daten (Keywords, Rankings, Backlinks) im Bereich Website &amp; SEO verwendet.{" "}
          <a href="https://www.semrush.com/api-analytics/" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">API-Key hier finden ↗</a>
        </p>
      </CardContent>
    </Card>
  );
}

export default function LlmKeysPage() {
  const [data, setData] = useState({ providers: [], fernet_ready: true });
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});  // provider → string typed by user
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/llm-keys`, { withCredentials: true }),
      axios.get(`${API}/admin/routing-overview`, { withCredentials: true }),
    ])
      .then(([keysRes, overviewRes]) => {
        setData(keysRes.data);
        setOverview(overviewRes.data?.tasks || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setEdit = (prov, val) => setEdits((e) => ({ ...e, [prov]: val }));

  const saveAll = async () => {
    const keys = {};
    Object.entries(edits).forEach(([k, v]) => {
      const s = (v ?? "").trim();
      if (s && s !== SENTINEL) keys[k] = s;
    });
    if (Object.keys(keys).length === 0) {
      toast.info("Keine Aenderungen zum Speichern");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/llm-keys`, { keys }, { withCredentials: true });
      toast.success("Schluessel gespeichert (verschluesselt)");
      setEdits({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  const test = async (prov) => {
    setTesting(prov);
    try {
      await axios.post(`${API}/admin/llm-keys/test/${prov}`, {}, { withCredentials: true });
      toast.success(`${prov.toUpperCase()} OK — Verbindung erfolgreich`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen");
      load();
    }
    setTesting(null);
  };

  const remove = async (prov) => {
    if (!window.confirm(`Schluessel fuer ${prov.toUpperCase()} loeschen? App faellt dann auf Emergent-Universal-Key zurueck.`)) return;
    try {
      await axios.delete(`${API}/admin/llm-keys/${prov}`, { withCredentials: true });
      toast.success("Geloescht");
      setEdits((e) => { const n = { ...e }; delete n[prov]; return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Loeschen fehlgeschlagen");
    }
  };

  const toggleStrict = async (v) => {
    try {
      await axios.put(`${API}/admin/llm-keys`, { keys: {}, strict_own_keys: v }, { withCredentials: true });
      toast.success(v ? "Nur eigene Keys aktiv — Emergent-Fallback deaktiviert" : "Emergent-Fallback wieder aktiv");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Umschalten fehlgeschlagen");
    }
  };

  return (
    <div data-testid="llm-keys-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3">
          <KeyRound className="w-8 h-8 text-[#2563EB]" /> KI-API-Schluessel
        </h1>
        <p className="text-[var(--studio-text-muted)] mt-1">
          Hinterlege deine eigenen API-Schluessel zentral. Alle KI-Aufrufe (Captions, Bilder, Videos, Auto-Fill, Brand-Voice)
          nutzen automatisch den passenden Schluessel je Aufgabe. Fehlt ein Schluessel, faellt die App auf den Emergent-Universal-Key zurueck.
        </p>
      </div>

      <PageHelp title="Welcher Provider macht was? Wann brauche ich welchen Key?" storageKey="llm-keys">
        <h4>Wie das System entscheidet</h4>
        <p>Pro Aufgabe (Caption, Bild, Video, Hashtag, Bild-Analyse, Wettbewerber-Check, …) gibt es eine bevorzugte Reihenfolge:</p>
        <ol>
          <li><b>Firma-spezifische Wahl</b> (auf der Firmen-Detail-Seite gesetzt) — sticht alles.</li>
          <li><b>Globale Wahl pro Aufgabe</b> (auf <code>/modell-routing</code>) — z. B. „Bilder immer ueber Gemini".</li>
          <li><b>Hardcoded Defaults</b> — Captions = GPT-5.2, Bilder = Gemini Nano Banana, Video = Sora-2 (unter «KI-Modelle» umstellbar, z.B. auf Veo 3.1).</li>
        </ol>
        <h4>Was passiert, wenn ein Schluessel fehlt?</h4>
        <ul>
          <li>System nimmt automatisch den <b>Emergent-Universal-Key</b> (= dein Emergent-Credit).</li>
          <li>Im Badge siehst du <code>Emergent</code> statt <code>Custom</code>.</li>
        </ul>
        <h4>Welche Provider eintragen — Entscheidungshilfe</h4>
        <ul>
          <li><b>OpenAI</b> (sk-…) — wird fuer fast alle Texte gebraucht (Captions, Hashtags, Brand-Voice, Wettbewerber). Holen unter <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com</a>. <i>Empfohlen.</i></li>
          <li><b>Gemini</b> (AIza…) — Bild-Generierung (Nano Banana, sehr guenstig) + Bild-Analyse beim Upload. Holen unter <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">aistudio.google.com</a>.</li>
          <li><b>Anthropic</b> (sk-ant-…) — Claude. Nur noetig, wenn du fuer manche Captions Claude statt GPT willst.</li>
          <li><b>fal.ai</b> — alternative Bild- (Flux/SDXL) und Video-Modelle (Kling/Luma). Nur, wenn dir Gemini-Stil oder Sora-Preis nicht passen.</li>
        </ul>
        <h4>So traegst du einen Schluessel ein</h4>
        <ol>
          <li>Holen vom Provider (Link oben), kopieren.</li>
          <li>Hier ins richtige Feld einfuegen → <b>Speichern</b>.</li>
          <li>Klick <b>Test</b> → gruener Haken = funktioniert.</li>
          <li>Optional auf <code>/modell-routing</code> festlegen, fuer welche Tasks dieser Provider benutzt wird.</li>
        </ol>
      </PageHelp>

      <Card className="border-emerald-200 bg-emerald-50/30 shadow-none">
        <CardContent className="py-3 px-4 flex items-start gap-2 text-xs text-emerald-800">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Sicherheit</p>
            <p>Schluessel werden mit Fernet (AES-128) verschluesselt in MongoDB gespeichert. Die API gibt nie den Klartext zurueck — nur eine maskierte Vorschau.</p>
          </div>
        </CardContent>
      </Card>

      {!data.fernet_ready && (
        <Card className="border-amber-300 bg-amber-50 shadow-none">
          <CardContent className="py-3 px-4 flex items-center gap-2 text-amber-800 text-sm">
            <AlertTriangle className="w-4 h-4" />
            EMAIL_ENCRYPTION_KEY fehlt im Backend — Speichern nicht moeglich.
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--studio-text-muted)]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Konfiguration…
        </div>
      ) : (
        <>
        <Card className={data.strict_own_keys ? "border-emerald-300 bg-emerald-50/40" : "border-amber-200 bg-amber-50/30"}>
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <Lock className={`w-5 h-5 mt-0.5 shrink-0 ${data.strict_own_keys ? "text-emerald-600" : "text-amber-500"}`} />
            <div className="flex-1">
              <p className="text-sm font-semibold">Nur eigene API-Keys verwenden</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {data.strict_own_keys
                  ? "Aktiv: KI-Funktionen laufen ausschliesslich über eure eigenen Keys. Fehlt ein Key, wird die Funktion mit klarer Fehlermeldung gestoppt (kein Emergent-Fallback)."
                  : "Aus: Fehlt ein eigener Key, nutzt die App den Emergent Universal Key als Fallback (Abrechnung über Emergent-Guthaben)."}
              </p>
            </div>
            <Switch
              checked={!!data.strict_own_keys}
              onCheckedChange={toggleStrict}
              data-testid="strict-own-keys-switch"
            />
          </CardContent>
        </Card>

        <div className="space-y-3" data-testid="llm-keys-list">
          {data.providers.map((p) => {
            const hint = PROVIDER_HINTS[p.provider] || {};
            const isEdited = (edits[p.provider] ?? "").length > 0 && edits[p.provider] !== SENTINEL;
            return (
              <Card
                key={p.provider}
                className="border-[var(--studio-border)] shadow-none"
                data-testid={`provider-card-${p.provider}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2 justify-between">
                    <span className="flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-[#2563EB]" />
                      {p.label}
                    </span>
                    {p.configured ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid={`badge-configured-${p.provider}`}>
                        Konfiguriert
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[var(--studio-text-muted)]" data-testid={`badge-fallback-${p.provider}`}>
                        Fallback aktiv
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="password"
                      value={edits[p.provider] ?? (p.configured ? SENTINEL : "")}
                      onChange={(e) => setEdit(p.provider, e.target.value)}
                      onFocus={(e) => {
                        if (e.target.value === SENTINEL) setEdit(p.provider, "");
                      }}
                      placeholder={hint.placeholder || "API-Schluessel"}
                      className="flex-1 min-w-[260px] font-mono text-xs"
                      data-testid={`input-key-${p.provider}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={testing === p.provider}
                      onClick={() => test(p.provider)}
                      data-testid={`test-${p.provider}-btn`}
                    >
                      {testing === p.provider ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                      Testen
                    </Button>
                    {p.configured && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(p.provider)}
                        className="text-red-500"
                        data-testid={`remove-${p.provider}-btn`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                    <div className="text-[var(--studio-text-muted)] flex items-center gap-2">
                      {p.configured && <span data-testid={`preview-${p.provider}`}>Vorschau: <code>{p.preview}</code></span>}
                      {hint.url && (
                        <a href={hint.url} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">
                          Schluessel hier holen ↗
                        </a>
                      )}
                      {isEdited && <span className="text-amber-700">geaendert (nicht gespeichert)</span>}
                    </div>
                    {p.last_test_at && (
                      <div className="flex items-center gap-1">
                        {p.last_test_ok ? (
                          <span className="text-emerald-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> OK · {new Date(p.last_test_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1" title={p.last_test_error || ""}>
                            <XCircle className="w-3 h-3" /> Fehler · {new Date(p.last_test_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <SemrushKeyCard />

          <TransferCard />

          <div className="flex justify-end pt-2">
            <Button
              onClick={saveAll}
              disabled={saving || Object.keys(edits).length === 0}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="save-all-keys-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Alle Aenderungen speichern
            </Button>
          </div>
        </div>
        </>
      )}

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Cpu className="w-4 h-4 text-[#2563EB]" /> Live-Status pro KI-Aufgabe
          </CardTitle>
          <p className="text-xs text-[var(--studio-text-muted)] mt-1">
            Was nutzt die App gerade wirklich? Orange = laeuft ueber Emergent-Universal-Key.
            Gruen = dein eigener Schluessel wird verwendet.
          </p>
        </CardHeader>
        <CardContent className="space-y-1">
          {overview.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]">Lade...</p>
          ) : overview.map((t) => (
            <div
              key={t.task}
              className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--studio-bg)]/40 text-xs"
              data-testid={`overview-row-${t.task}`}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium">{t.label}</span>
                <span className="ml-2 text-[10px] text-[var(--studio-text-muted)] font-mono">
                  {t.provider} / {t.model}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {t.routing_source === "custom" && (
                  <Badge variant="outline" className="text-[9px]" data-testid={`routing-custom-${t.task}`}>Custom-Routing</Badge>
                )}
                {t.key_source === "own" ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[9px]" data-testid={`key-own-${t.task}`}>
                    <ShieldCheck className="w-3 h-3 mr-0.5" /> Eigener Key
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border-0 text-[9px]" data-testid={`key-emergent-${t.task}`}>
                    <Zap className="w-3 h-3 mr-0.5" /> Emergent
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-base">Verwendung pro Aufgabe</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-[var(--studio-text-muted)] space-y-1">
          <p><b>OpenAI</b> — Captions/Reels (GPT-5.2), Vision-Bildanalyse, KI-Auto-Fill, Brand-Voice-Training, Hashtag-Vorschlaege, Sora-2 Video-Generation</p>
          <p><b>Gemini</b> — Inline Bildgenerierung (Nano Banana), optional als Caption-Fallback</p>
          <p><b>Anthropic</b> — Optional als Caption-Provider (Claude Sonnet/Haiku in KI-Provider-Konfig waehlbar)</p>
          <p><b>fal.ai</b> — Reserved fuer kuenftige Bild/Video-Provider (Schluessel kann bereits gespeichert werden)</p>
        </CardContent>
      </Card>
    </div>
  );
}
